import { ColorField } from "@/components/cms/theme/ColorField";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import {
  normalizeButtonAppearance,
  type BorderSide,
  type BorderStyle,
  type ButtonAppearance,
  type ButtonBorder,
  type ButtonRadius,
} from "@/lib/cms-puck/blocks/button";
import { Label } from "@/components/ui/label";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * Champ Puck custom : apparence avancée du Bouton — bordures (largeur PAR CÔTÉ +
 * style & couleur partagés), radius PAR COIN, et couleurs au survol (hover).
 * Valeur = `ButtonAppearance`. Couleurs via `ColorField` (palette du thème).
 *
 * `sections` : sous-ensemble affiché. Depuis l'unification du style partagé, le
 * bloc Bouton n'expose ICI que le survol (bordures/rayons = onglet Design
 * partagé, prioritaire sur l'apparence legacy) ; PaymentButton garde la section
 * (l'apparence y style les CARTES, pas le bloc).
 */
export type ButtonAppearanceSection = "border" | "radius" | "hover";

const SIDE_LABELS: Array<[keyof ButtonBorder, string]> = [
  ["top", "Haut"],
  ["right", "Droite"],
  ["bottom", "Bas"],
  ["left", "Gauche"],
];

const CORNER_LABELS: Array<[keyof ButtonRadius, string]> = [
  ["tl", "Haut G."],
  ["tr", "Haut D."],
  ["br", "Bas D."],
  ["bl", "Bas G."],
];

/** Clés + fallbacks — ne pas résoudre cmsNew() au niveau module. */
const BORDER_STYLE_OPTIONS: Array<{
  value: BorderStyle;
  labelKey: CmsNewKey;
  fallback: string;
}> = [
  { value: "solid", labelKey: "cms.new.auto.label.plein", fallback: "Plein" },
  { value: "dashed", labelKey: "cms.new.auto.label.tirets", fallback: "Tirets" },
  { value: "dotted", labelKey: "cms.new.auto.label.points", fallback: "Points" },
];

const SECTION_CLASS =
  "space-y-3 rounded-xl border border-slate-200/70 bg-white p-3 shadow-sm";

function NumberCell({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (n: number | null) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] font-medium text-slate-500">{label}</Label>
      <input
        type="number"
        min={0}
        value={value ?? ""}
        placeholder={cmsNew("cms.new.auto.placeholder.26aeab", "—")}
        className={cn(CMS_PALETTE_TEXT_INPUT_CLASS, "h-9 text-center tabular-nums")}
        onChange={(e) => {
          const raw = e.target.value;
          const n = raw === "" ? null : Number(raw);
          onChange(n != null && Number.isFinite(n) && n >= 0 ? n : null);
        }}
      />
    </div>
  );
}

export function ButtonAppearanceField({
  value,
  onChange,
  sections = ["border", "radius", "hover"],
}: {
  value: ButtonAppearance | undefined;
  onChange: (value: ButtonAppearance) => void;
  sections?: ButtonAppearanceSection[];
}) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const colors: CmsColor[] = theme?.colors ?? [];
  const appearance = normalizeButtonAppearance(value);
  const { border, radius, hover } = appearance;

  const setBorderWidth = (side: keyof ButtonBorder, width: number | null) =>
    onChange({
      ...appearance,
      border: { ...border, [side]: { ...border[side], width } },
    });

  // Style & couleur de bordure PARTAGÉS sur les 4 côtés (UX simple ; le modèle
  // reste par côté). On lit la valeur du côté `top` comme représentative.
  const setBorderAllSides = (patch: Partial<BorderSide>) => {
    const next = {} as ButtonBorder;
    for (const [side] of SIDE_LABELS) {
      next[side] = { ...border[side], ...patch };
    }
    onChange({ ...appearance, border: next });
  };

  const setRadius = (corner: keyof ButtonRadius, n: number | null) =>
    onChange({ ...appearance, radius: { ...radius, [corner]: n } });

  const setHover = (patch: Partial<ButtonAppearance["hover"]>) =>
    onChange({ ...appearance, hover: { ...hover, ...patch } });

  return (
    <div className="space-y-4 p-1">
      {/* Bordures */}
      {sections.includes("border") ? (
      <section className={SECTION_CLASS}>
        <h4 className="text-sm font-semibold text-slate-900">{cmsNew("cms.new.auto.text.bordures_px", "Bordures (px)")}</h4>
        <div className="grid grid-cols-4 gap-2">
          {SIDE_LABELS.map(([side, label]) => (
            <NumberCell
              key={side}
              label={label}
              value={border[side].width}
              onChange={(width) => setBorderWidth(side, width)}
            />
          ))}
        </div>
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] font-medium text-slate-500">
              Style
            </Label>
            <Select
              value={border.top.style}
              onValueChange={(v) => setBorderAllSides({ style: v as BorderStyle })}
            >
              <SelectTrigger className="h-9 w-28 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BORDER_STYLE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {cmsNew(o.labelKey, o.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <ColorField
              label={cmsNew("cms.new.auto.label.couleur_de_bordure", "Couleur de bordure")}
              colors={colors}
              value={border.top.color}
              onChange={(color) => setBorderAllSides({ color })}
            />
          </div>
        </div>
      </section>
      ) : null}

      {/* Radius */}
      {sections.includes("radius") ? (
      <section className={SECTION_CLASS}>
        <h4 className="text-sm font-semibold text-slate-900">
          Arrondi des coins (px)
        </h4>
        <div className="grid grid-cols-4 gap-2">
          {CORNER_LABELS.map(([corner, label]) => (
            <NumberCell
              key={corner}
              label={label}
              value={radius[corner]}
              onChange={(n) => setRadius(corner, n)}
            />
          ))}
        </div>
      </section>
      ) : null}

      {/* Hover */}
      {sections.includes("hover") ? (
      <section className={SECTION_CLASS}>
        <h4 className="text-sm font-semibold text-slate-900">{cmsPhrase(cmsNew, "buttonHoverSection")}</h4>
        <ColorField
          label={cmsNew("cms.new.auto.label.fond_survol", "Fond (survol)")}
          colors={colors}
          value={hover.background}
          onChange={(background) => setHover({ background })}
        />
        <ColorField
          label={cmsNew("cms.new.auto.label.texte_survol", "Texte (survol)")}
          colors={colors}
          value={hover.text}
          onChange={(text) => setHover({ text })}
        />
        <ColorField
          label={cmsNew("cms.new.auto.label.bordure_survol", "Bordure (survol)")}
          colors={colors}
          value={hover.borderColor}
          onChange={(borderColor) => setHover({ borderColor })}
        />
      </section>
      ) : null}
    </div>
  );
}
